/**
 * @file jobApplication.tsx
 *
 * @fileoverview Exports the job application feature related utils.
 */
import React, { useContext, useState } from 'react';
import { IntlShape } from 'react-intl';
import * as Yup from 'yup';
import BooleanWidget from '~/jobiqo/components/Form/Widgets/Boolean';
import StringWidget from '~/jobiqo/components/Form/Widgets/String';
import { FIELD_JOB_DURATION } from '~/jobiqo/components/Job/Form/components/DurationInput';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { useConfig } from '~/jobiqo/context/config';
import RichFormattedMessage from '~/jobiqo/components/RichFormattedMessage';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';

const QuickApplyField = ({ field, values }) =>
  values.field_job_application_workflow === 'quick' ? (
    <div key={field.id}>
      <BooleanWidget entityField={field} />
      <Spacing size="medium" />
    </div>
  ) : null;

const InternalField = ({ field, values }) =>
  values.field_job_application_workflow === 'quick' ||
  values.field_job_application_workflow === 'internal' ? (
    <div key={field.id}>
      <BooleanWidget entityField={field} />
      <Spacing size="medium" />
    </div>
  ) : null;

/**
 * Overrides the default job form job application fields.
 * If the user selects "external" for the "Application workflow" field, then show
 * the "Application link" field. If the user selects "internal", then show the
 * "Application email" field
 * @returns An object with the overrides for the job form job application fields.
 */
export function getJobFormApplicationOverrides() {
  const { user }: UserContextInterface = useContext(UserContext);
  const { getConfigByName } = useConfig();

  const removeQuickApplyCVoption = !!getConfigByName(
    'quick_application_remove_cv_option',
  )?.value[0];

  const removeQuickApplyCoverLetter = !!getConfigByName(
    'quick_application_remove_cover_letter',
  )?.value[0];

  const removeQuickApplyQualifyingQuestions = !!getConfigByName(
    'quick_application_remove_qualifying_questions',
  )?.value[0];

  return {
    field_job_application_link: ({ field, values, setFieldValue }) => {
      if (values.field_job_application_workflow === 'external') {
        return (
          <div key={field.id}>
            <StringWidget required={true} entityField={field} />
            <Spacing size="medium" />
          </div>
        );
      } else {
        // Clear the value to avoid validation issues.
        setFieldValue('field_job_application_link', '');
        return null;
      }
    },
    field_job_application_email: ({
      field,
      values,
      setFieldValue,
      formState,
      reset,
    }) => {
      const jobApplicationWorkflow = values.field_job_application_workflow;
      const isInternal =
        jobApplicationWorkflow === 'internal' ||
        jobApplicationWorkflow === 'quick';
      const required = isInternal;
      const [defaultValue, setDefaultValue] = useState<string | undefined>(
        undefined,
      );

      if (isInternal) {
        if (
          values?.['field_job_application_email']?.length === 0 &&
          !formState?.touchedFields?.['field_job_application_email'] &&
          user?.email &&
          defaultValue !== user?.email
        ) {
          setDefaultValue(user?.email);
        }

        return (
          <div>
            <StringWidget
              defaultValue={defaultValue}
              required={required}
              type="email"
              entityField={field}
            />
            <Spacing size="medium" />
          </div>
        );
      } else {
        return null;
      }
    },
    field_job_application_require_cv: ({ field, values }) => {
      // Render the quick apply group title for the first element.
      // Show group when there's no hidden options.
      if (
        values.field_job_application_workflow === 'quick' &&
        (!removeQuickApplyCVoption ||
          !removeQuickApplyCoverLetter ||
          !removeQuickApplyQualifyingQuestions)
      ) {
        return (
          <>
            <Text tag="h4" fontSize={3}>
              <RichFormattedMessage
                id="job_posting_quick_apply_title"
                defaultMessage="Which of the following Quick Apply Features would you like to enable?"
              />
              <Spacing size="medium" />
            </Text>
            {removeQuickApplyCVoption
              ? null
              : QuickApplyField({ field, values })}
          </>
        );
      }
      return null;
    },
    field_job_app_require_cover: ({ field, values }) => {
      if (values.field_job_application_workflow === 'quick') {
        if (removeQuickApplyCoverLetter) {
          return null;
        }
        return QuickApplyField({ field, values });
      }

      return QuickApplyField({ field, values });
    },
    field_job_app_require_questions: ({ field, values }) => {
      const jobApplicationWorkflow = values.field_job_application_workflow;
      const isInternal =
        jobApplicationWorkflow === 'internal' ||
        jobApplicationWorkflow === 'quick';

      if (isInternal) {
        if (removeQuickApplyQualifyingQuestions) {
          return null;
        }
        return InternalField({ field, values });
      }
      return InternalField({ field, values });
    },
  };
}

/**
 * It returns a Yup validation schema for the job form
 * job application fields.
 * @param {IntlShape} intl - IntlShape
 * @param maximumJobDuration - The maximum number of days a job can be online.
 * @returns An object with the Yup validation schema for the job form
 * job application fields.
 */
export function getJobFormApplicationValidationSchema(
  intl: IntlShape,
  maximumJobDuration,
) {
  const scheduledJobFeatureActive = getCurrentFrontendConfig().JOBS
    .SCHEDULED_JOB_FEATURE_ACTIVE;
  return {
    field_job_application_link: Yup.string().when(
      'field_job_application_workflow',
      (field_job_application_workflow) =>
        field_job_application_workflow === 'external'
          ? Yup.string().required(
              intl.formatMessage({
                id: 'field_required',
                defaultMessage: 'Field is required',
              }),
            )
          : Yup.string().nullable(),
    ),
    field_job_application_email: Yup.string().when(
      'field_job_application_workflow',
      (field_job_application_workflow) =>
        field_job_application_workflow === 'internal' ||
        field_job_application_workflow === 'quick'
          ? Yup.string().required(
              intl.formatMessage({
                id: 'field_required',
                defaultMessage: 'Field is required',
              }),
            )
          : Yup.string().nullable(),
    ),
    ...(scheduledJobFeatureActive && {
      [FIELD_JOB_DURATION]: Yup.number().max(
        maximumJobDuration,
        intl.formatMessage(
          {
            id: 'job_duration_max_value',
            defaultMessage: 'The maximum number of days online is {maxValue}.',
          },
          { maxValue: maximumJobDuration },
        ),
      ),
    }),
  };
}
